/**
 * Pool Frame Heartbeat
 * --------------------
 * Envoie un heartbeat toutes les 5 secondes au parent (Arsenal Media watchdog)
 * lorsque DynastyPool est chargé dans une iframe.
 *
 * Ce module est volontairement indépendant de React : il s'initialise dès son
 * import et ne dépend d'aucun cycle de vie de composant. Il est chargé depuis
 * `index.html` via un `<script type="module">` afin de contourner la CSP qui
 * bloque les scripts inline.
 *
 * API de test exposée sur `window.__dpHeartbeat` :
 *   - `stop()` : arrête l'intervalle heartbeat (utilisé par Phil pour simuler
 *     une mort silencieuse du frame pendant la recette du watchdog).
 */

const HEARTBEAT_INTERVAL_MS = 5000;
const PARENT_ORIGIN = 'https://arsenalsports.ca';

export interface DpHeartbeatApi {
  stop: () => void;
}

declare global {
  interface Window {
    __dpHeartbeat?: DpHeartbeatApi;
  }
}

/**
 * Démarre le heartbeat. Encapsule l'ID d'intervalle dans une closure pour
 * éviter les variables globales inutiles, puis expose une API `stop()` sur
 * `window.__dpHeartbeat`.
 *
 * Retourne l'API de contrôle (utile pour les tests unitaires).
 */
export function startPoolFrameHeartbeat(): DpHeartbeatApi {
  // Environnement non-navigateur (SSR, tests Node sans DOM) : no-op.
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return { stop: () => {} };
  }

  const intervalId = window.setInterval(() => {
    if (document.visibilityState !== 'visible') return;

    const root = document.getElementById('root');
    const alive = (root?.childElementCount ?? 0) > 0;

    window.parent.postMessage(
      {
        source: 'dynastypool',
        type: 'heartbeat',
        alive,
        href: location.href,
      },
      PARENT_ORIGIN,
    );
  }, HEARTBEAT_INTERVAL_MS);

  const api: DpHeartbeatApi = {
    stop() {
      window.clearInterval(intervalId);
    },
  };

  window.__dpHeartbeat = api;
  return api;
}

// Auto-initialisation : le simple fait d'importer ce module déclenche le
// heartbeat, exactement comme le faisait le script inline précédent.
startPoolFrameHeartbeat();
